/**
 * Tiny SEO hook for public pages such as LandingPage, TopicHub, and NotFound.
 *
 * We don't ship react-helmet-async because the whole app is a single
 * SPA bundle and adding another dep just to mutate <head> tags is
 * overkill. This hook does the same job inline: on mount it upserts
 * the title, meta description, canonical, OG, Twitter tags, and an
 * optional JSON-LD <script>, then on unmount it rolls everything
 * back to the previous value so navigating back to Dashboard
 * doesn't leak a stale <title>.
 *
 * Important caveat: because this is client-side only, Googlebot will
 * pick up the updated tags only after JS hydration. Modern Googlebot
 * does hydrate, but for heavier SEO lift you'd want real SSR or a
 * prerender service. This hook is the 90% solution without the
 * infrastructure cost.
 *
 * Usage:
 *   useSeo({
 *     title: "Page title",
 *     description: "150-160 char summary",
 *     canonicalPath: "/cert/saa-c03",
 *     ogImage: "/og/default.png",
 *     jsonLd: { "@context": "https://schema.org", ... },
 *   });
 */
import { useEffect } from "react";

const SITE_NAME = "ExamNova";
// Keep this in sync with the canonical production host. If the
// frontend is served from a different host (staging, preview) the
// canonical still points to prod so we don't split link equity
// across environments. Non-www is the live primary: Netlify 301s
// www.examnova.pro -> examnova.pro and the backend sitemap emits
// non-www <loc>s, so canonical/OG/JSON-LD URLs must match (non-www)
// or we point Google at a host that immediately redirects.
const SITE_ORIGIN =
    process.env.REACT_APP_PUBLIC_SITE_ORIGIN || "https://examnova.pro";

const JSON_LD_ID = "seo-json-ld";

// Public URLs are served lowercase with a trailing slash (Netlify pretty
// URLs 301 anything else into that form). Canonical/og:url/JSON-LD URLs
// must emit the FINAL form so Google never sees a canonical that redirects.
// Mirrors _canonical_sitemap_path in backend/server.py and canonicalPath
// in frontend/scripts/prerender-seo.mjs.
export const canonicalHref = (p = "/") => {
    let path = String(p).startsWith("/") ? String(p) : `/${p}`;
    path = path.toLowerCase();
    if (!path.endsWith("/")) path += "/";
    return `${SITE_ORIGIN}${path}`;
};

function upsertMeta(attr, key, value) {
    if (!value) return null;
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    const previous = el ? el.getAttribute("content") : null;
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute("content", value);
    return { el, previous, created: !previous && !el.dataset.seoOwned };
}

function upsertLink(rel, href) {
    if (!href) return null;
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    const previous = el ? el.getAttribute("href") : null;
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
    return { el, previous };
}

export function useSeo({
    title,
    description,
    canonicalPath,
    ogType = "website",
    ogImage,
    ogImageAlt,
    noIndex = false,
    jsonLd,
} = {}) {
    useEffect(() => {
        const prevTitle = document.title;
        const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
        document.title = fullTitle;

        const canonicalUrl = canonicalPath ? canonicalHref(canonicalPath) : null;

        const touched = [];
        const touchedLinks = [];

        const add = (attr, key, value) => {
            const t = upsertMeta(attr, key, value);
            if (t) touched.push({ attr, key, ...t });
        };
        const addLink = (rel, href) => {
            const t = upsertLink(rel, href);
            if (t) touchedLinks.push({ rel, ...t });
        };

        add("name", "description", description);
        add("property", "og:site_name", SITE_NAME);
        add("property", "og:type", ogType);
        add("property", "og:title", title || SITE_NAME);
        add("property", "og:description", description);
        if (canonicalUrl) add("property", "og:url", canonicalUrl);
        if (ogImage) {
            const absImg = ogImage.startsWith("http")
                ? ogImage
                : `${SITE_ORIGIN}${ogImage.startsWith("/") ? ogImage : `/${ogImage}`}`;
            add("property", "og:image", absImg);
            if (ogImageAlt) add("property", "og:image:alt", ogImageAlt);
        }
        add("name", "twitter:card", ogImage ? "summary_large_image" : "summary");
        add("name", "twitter:title", title || SITE_NAME);
        add("name", "twitter:description", description);
        if (noIndex) add("name", "robots", "noindex,nofollow");

        if (canonicalUrl) addLink("canonical", canonicalUrl);

        // JSON-LD — single managed script tag. Overwrite its text on
        // every mount so swapping between posts updates the schema
        // instead of stacking tags.
        let prevJsonLd = null;
        let jsonLdScript = document.head.querySelector(`script#${JSON_LD_ID}`);
        if (jsonLdScript) prevJsonLd = jsonLdScript.textContent;
        if (jsonLd) {
            if (!jsonLdScript) {
                jsonLdScript = document.createElement("script");
                jsonLdScript.id = JSON_LD_ID;
                jsonLdScript.type = "application/ld+json";
                document.head.appendChild(jsonLdScript);
            }
            try {
                jsonLdScript.textContent = JSON.stringify(jsonLd);
            } catch (_) {
                jsonLdScript.textContent = "";
            }
        }

        return () => {
            // Roll back on unmount so the next route starts from a
            // clean slate. We restore previous values where we had
            // them, and blank-out the ones we created.
            document.title = prevTitle;
            touched.forEach(({ el, previous }) => {
                if (previous === null) {
                    if (el && el.parentNode) el.parentNode.removeChild(el);
                } else {
                    el.setAttribute("content", previous);
                }
            });
            touchedLinks.forEach(({ el, previous }) => {
                if (previous === null) {
                    if (el && el.parentNode) el.parentNode.removeChild(el);
                } else {
                    el.setAttribute("href", previous);
                }
            });
            if (jsonLdScript) {
                if (prevJsonLd === null) {
                    if (jsonLdScript.parentNode) {
                        jsonLdScript.parentNode.removeChild(jsonLdScript);
                    }
                } else {
                    jsonLdScript.textContent = prevJsonLd;
                }
            }
        };
        // We intentionally re-run on every prop change so moving from
        // one public page to another updates the tags.
    }, [title, description, canonicalPath, ogType, ogImage, ogImageAlt, noIndex, jsonLd]);
}
